import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Input,
  tokens,
} from '@fluentui/react-components';
import {
  setTaxReturnExtension,
  clearTaxReturnExtension,
} from '../../services/api';
import type { ExtensionFilingMethod, ExtensionInfo } from '../../types';
import { CalendarDateInput } from '../dates/CalendarDateInput';

const FILING_METHODS: { value: ExtensionFilingMethod; label: string }[] = [
  { value: 'e-filed', label: 'E-filed' },
  { value: 'paper', label: 'Paper' },
  { value: 'previously-filed', label: 'Previously filed' },
];

interface ExistingExtension {
  extensionFiledAt?: string | null;
  extensionFilingMethod?: ExtensionFilingMethod | null;
  extensionConfirmation?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taxReturnId: string;
  existing?: ExistingExtension;
}

export default function RecordExtensionDialog({
  open,
  onOpenChange,
  taxReturnId,
  existing,
}: Props) {
  const qc = useQueryClient();
  const [date, setDate] = useState<string>(
    existing?.extensionFiledAt ? existing.extensionFiledAt.slice(0, 10) : ''
  );
  const [method, setMethod] = useState<ExtensionFilingMethod>(
    (existing?.extensionFilingMethod as ExtensionFilingMethod) ?? 'e-filed'
  );
  const [confirmation, setConfirmation] = useState<string>(
    existing?.extensionConfirmation ?? ''
  );
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: (body: ExtensionInfo) => setTaxReturnExtension(taxReturnId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['taxReturn', taxReturnId] });
      onOpenChange(false);
    },
    onError: (e: Error) => setError(e.message),
  });

  const clearMutation = useMutation({
    mutationFn: () => clearTaxReturnExtension(taxReturnId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['taxReturn', taxReturnId] });
      onOpenChange(false);
    },
    onError: (e: Error) => setError(e.message),
  });

  const handleSave = () => {
    setError(null);
    if (!date) {
      setError('Extension filed at is required.');
      return;
    }
    const parsed = new Date(date);
    if (parsed.getTime() > Date.now()) {
      setError('Extension filed at cannot be in the future.');
      return;
    }
    saveMutation.mutate({
      extensionFiledAt: parsed.toISOString(),
      extensionFilingMethod: method,
      extensionConfirmation: confirmation || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Record extension</DialogTitle>
          <DialogContent>
            {error && (
              <div
                role="alert"
                style={{
                  color: tokens.colorPaletteRedForeground1,
                  backgroundColor: tokens.colorPaletteRedBackground1,
                  border: `1px solid ${tokens.colorPaletteRedBorder1}`,
                  borderRadius: '4px',
                  padding: '8px 12px',
                  marginBottom: '12px',
                  fontSize: tokens.fontSizeBase300,
                }}
              >
                {error}
              </div>
            )}
            <Field label="Extension filed at" required>
              <CalendarDateInput
                value={date}
                onChange={setDate}
                aria-label="Extension filed at"
              />
            </Field>
            <Field label="Filing method" required>
              {/* Plain <select> keeps fireEvent.change working in tests */}
              <select
                value={method}
                onChange={e => setMethod(e.target.value as ExtensionFilingMethod)}
                aria-label="Filing method"
                style={{ width: '100%', padding: '6px 8px', fontSize: '14px' }}
              >
                {FILING_METHODS.map(m => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Confirmation #">
              <Input
                value={confirmation}
                onChange={(_, data) => setConfirmation(data.value)}
                aria-label="Confirmation"
              />
            </Field>
          </DialogContent>
          <DialogActions>
            {existing?.extensionFiledAt && (
              <Button
                appearance="subtle"
                onClick={() => clearMutation.mutate()}
                disabled={clearMutation.isPending}
              >
                Clear extension
              </Button>
            )}
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
